<template>
  <modal-component
    :is-displayed="isSpotIntegrationModalOpened"
    :no-padding="true"
    :remove-outside-click="true"
    :title="$t('ParkingSpots.SpotIntegrationTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <form-wrapper :is-form-in-modal="true">
      <form @submit.prevent="handleSubmit">
        <form-divider>
          <div class="relative items-center py-4">
            <label
              class="whitespace-nowrap text-sm font-medium not-italic text-gray-800"
              >{{ $t("ParkingSpots.ParkingSpace") }}</label
            >
            <div class="flex items-center justify-center">
              <small-loader
                v-if="isParkingSpaceListLoading"
                height="h-8"
                width="w-8"
              />
              <v-select
                v-else
                multiple
                :closeOnSelect="false"
                v-model="selectedSpaces"
                :class="`mt-1 w-full`"
                :options="parkingSpaces"
                :reduce="(space : any) => space.id"
                label="name"
                @option:selected="filterSpots"
                @option:deselected="filterSpots"
              ></v-select>
            </div>
            <div
              v-if="isSpotListLoading"
              class="mt-10 flex w-full justify-center"
            >
              <small-loader />
            </div>
            <custom-table
              ref="table"
              v-else
              :allow-auto-resize="true"
              :is-action-list-not-displayed="true"
              :is-data-source-loading="isSpotListLoading"
              :table-data="parkingSpotsFiltered"
              :table-headers="tableHeaders"
              table-height="55vh"
              :no-selection="false"
              :multiselect="true"
              @selected-rows="updateSpots"
              key-expr="id"
              :selected-row-keys="selectedSpots"
              :force-refresh="true"
            />
          </div>
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form>
    </form-wrapper>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import InputField from "@/core/Components/FormElements/InputField.vue";
import VSelect from "vue-select";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import TheButton from "@/core/Components/TheButton.vue";
import CheckboxInput from "@/core/Components/FormElements/CheckboxInput.vue";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import { ParkingSpace } from "@/modules/Parkom/Submodules/ParkingSpaces/Services/ParkingSpaceTypes";
import ParkingSpaceService from "@/modules/Parkom/Submodules/ParkingSpaces/Services/ParkingSpaceService";
import ParkingSpotService from "@/modules/Parkom/Submodules/ParkingSpots/Services/ParkingSpotService";
import {
  ParkingSpot,
  ParkingSpotIntegration,
} from "@/modules/Parkom/Submodules/ParkingSpots/Services/ParkingSpotTypes";
import CustomTable from "@/core/Components/CustomTable.vue";

export default defineComponent({
  components: {
    CheckboxInput,
    ButtonWrapper,
    InputField,
    FormDivider,
    ModalComponent,
    FormWrapper,
    VSelect,
    SmallLoader,
    TheButton,
    CustomTable,
  },
  props: {
    isSpotIntegrationModalOpened: Boolean,
    refreshData: {
      type: Function,
      required: true,
    },
  },
  emits: ["toggle-modal"],
  data() {
    return {
      selectedSpaces: [] as number[],
      selectedSpots: [] as number[],
      parkingSpaceService: new ParkingSpaceService(),
      parkingSpotService: new ParkingSpotService(),
      parkingSpaces: [] as ParkingSpace[],
      parkingSpots: [] as ParkingSpot[],
      parkingSpotsFiltered: [] as ParkingSpot[],
      isParkingSpaceListLoading: false,
      isSpotListLoading: false,
      isSubmitLoading: false,
      toast: useToast(),
      tableHeaders: {
        id: { header: "ID", visible: false },
        space: { header: "ParkingSpots.Space", visible: true },
        spaceId: { header: "Sensors.SpaceId", visible: false },
        name: { header: "ParkingSpots.Name", visible: true },
        macAddress: { header: "ParkingSpots.MAC", visible: false },
        type: { header: "ParkingSpots.Type", visible: false },
        occupiedTimestamp: {
          header: "ParkingSpots.OccupiedTimestamp",
          visible: false,
        },
        occupied: { header: "ParkingSpots.Occupied", visible: false },
        index: { header: "ParkingSpots.Index", visible: false },
        public: { header: "ParkingSpots.Public", visible: false },
        latitude: { header: "ParkingSpots.Latitude", visible: false },
        longitude: { header: "ParkingSpots.Longitude", visible: false },
        integration: { header: "ParkingSpots.Integration", visible: true },
      },
    };
  },
  async created() {
    await Promise.allSettled([this.getParkingSpaces()]);
    try {
      await this.getParkingSpots();
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    }
  },
  watch: {},
  methods: {
    async handleSubmit() {
      this.isSubmitLoading = true;
      let changedSpots: ParkingSpotIntegration[] = [];
      await this.getParkingSpots();

      this.parkingSpotsFiltered.forEach((spot) => {
        const matchingSpot = this.parkingSpots.find(
          (orignialSpot) => orignialSpot.id == spot.id
        );
        if (matchingSpot && matchingSpot.integration != spot.integration) {
          const newSpot: ParkingSpotIntegration = {
            id: spot.id,
            integration: spot.integration,
          };
          changedSpots.push(newSpot);
        }
      });

      try {
        const response =
          await this.parkingSpotService.updateParkingSpotIntegrations(
            changedSpots
          );
        if (response.data < 1) {
          throw new Error("Something gone wrong with updating parking spots!");
        }
        this.$emit("toggle-modal");
        this.toast.success("Parking spots integrations updated successfully");
        await this.refreshData();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isSubmitLoading = false;
      }
    },
    async getParkingSpaces() {
      this.isParkingSpaceListLoading = true;
      try {
        this.parkingSpaces = await this.parkingSpaceService.getParkingSpaces({
          pageNumber: 1,
          pageSize: 100000,
        });
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isParkingSpaceListLoading = false;
      }
    },
    async getParkingSpots() {
      this.isSpotListLoading = true;
      try {
        this.parkingSpots = await this.parkingSpotService.getParkingSpots({
          pageNumber: 1,
          pageSize: 100000,
        });
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isSpotListLoading = false;
      }
    },
    filterSpots() {
      this.parkingSpotsFiltered = this.parkingSpots.filter(
        (spot: ParkingSpot) => this.selectedSpaces.includes(spot.spaceId)
      );
      this.selectSpots();
    },
    selectSpots() {
      let selectedIds = new Array<number>();
      this.parkingSpotsFiltered.forEach((spot) => {
        if (spot.integration) {
          selectedIds.push(spot.id);
        }
      });
      this.selectedSpots = selectedIds;
    },
    updateSpots(data: ParkingSpot[]) {
      this.parkingSpotsFiltered.forEach((spot) => {
        const found = data.some((newSpot) => newSpot.id == spot.id);
        if (found) {
          spot.integration = true;
        } else if (spot.integration) {
          spot.integration = false;
        }
      });
    },
  },
});
</script>
